import React, { useEffect, useRef, useState } from "react";
import { FileDown, FileText, FileSpreadsheet, FileType2, Presentation, ChevronDown, Loader2 } from "lucide-react";
import { API } from "../lib/api";

const FORMATS = [
    { key: "pdf", label: "PDF", Icon: FileText, hint: ".pdf · Board-ready" },
    { key: "xlsx", label: "Excel", Icon: FileSpreadsheet, hint: ".xlsx · Analysis" },
    { key: "docx", label: "Word", Icon: FileType2, hint: ".docx · Narrative" },
    { key: "pptx", label: "PowerPoint", Icon: Presentation, hint: ".pptx · Presentation" },
];

/**
 * Universal Export dropdown.
 * @param {string} kind - Export identifier matching backend /api/export/{kind}
 * @param {string} label - Button label (default "Export")
 * @param {string} size - "sm" | "md"
 */
export default function ExportMenu({ kind, label = "Export", size = "md", testId }) {
    const [open, setOpen] = useState(false);
    const [busy, setBusy] = useState(null);
    const ref = useRef(null);

    useEffect(() => {
        const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
        document.addEventListener("mousedown", onDoc);
        return () => document.removeEventListener("mousedown", onDoc);
    }, []);

    const trigger = async (fmt) => {
        setBusy(fmt);
        try {
            const token = localStorage.getItem("awc_token");
            const url = `${API}/export/${kind}?format=${fmt}`;
            const res = await fetch(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
            if (!res.ok) throw new Error(`Download failed (${res.status})`);
            const blob = await res.blob();
            const disp = res.headers.get("Content-Disposition") || "";
            const m = disp.match(/filename="?([^"]+)"?/);
            const filename = m ? m[1] : `AWC_${kind.replace(/\//g, "_")}.${fmt}`;
            const link = document.createElement("a");
            link.href = URL.createObjectURL(blob);
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            URL.revokeObjectURL(link.href);
            setOpen(false);
        } catch (err) {
            console.error("Export failed", err);
            alert(`Export failed: ${err.message}`);
        } finally {
            setBusy(null);
        }
    };

    const isSm = size === "sm";
    return (
        <div ref={ref} className="relative inline-block">
            <button
                data-testid={testId || `export-btn-${kind.replace(/[/]/g, "-")}`}
                onClick={() => setOpen((v) => !v)}
                className={`flex items-center gap-1.5 font-mono-fin uppercase tracking-[0.15em] border transition-colors ${
                    open ? "border-[var(--brand-amber)] text-[var(--brand-amber)] bg-[var(--brand-amber-dim)]"
                         : "border-panel text-[var(--text-secondary)] hover:text-[var(--brand-amber)] hover:border-[var(--brand-amber)]"
                } ${isSm ? "text-[9px] px-2 py-1" : "text-[10px] px-3 py-1.5"}`}
            >
                <FileDown size={isSm ? 10 : 12} />
                {label}
                <ChevronDown size={isSm ? 10 : 12} className={`transition-transform ${open ? "rotate-180" : ""}`} />
            </button>
            {open && (
                <div className="absolute right-0 top-full mt-1 z-50 min-w-[220px] bg-[var(--bg-panel)] border border-[var(--brand-amber)] shadow-2xl">
                    <div className="px-3 py-2 border-b border-panel">
                        <div className="text-[9px] font-mono-fin uppercase tracking-[0.2em] text-[var(--brand-amber)]">// DOWNLOAD FORMAT</div>
                        <div className="text-[9px] font-mono-fin text-[var(--text-muted)] mt-0.5 uppercase tracking-wider">{kind}</div>
                    </div>
                    {FORMATS.map((f) => (
                        <button
                            key={f.key}
                            data-testid={`export-${kind.replace(/[/]/g, "-")}-${f.key}`}
                            disabled={busy !== null}
                            onClick={() => trigger(f.key)}
                            className="w-full flex items-center gap-3 px-3 py-2.5 text-left hover:bg-[var(--bg-panel-hover)] border-b border-panel last:border-b-0 transition-colors disabled:opacity-40 disabled:cursor-wait"
                        >
                            {busy === f.key
                                ? <Loader2 size={14} className="text-[var(--brand-amber)] animate-spin shrink-0" />
                                : <f.Icon size={14} className="text-[var(--brand-amber)] shrink-0" />}
                            <div className="flex-1 min-w-0">
                                <div className="text-xs text-[var(--text-primary)] font-body">{f.label}</div>
                                <div className="text-[9px] font-mono-fin uppercase tracking-wider text-[var(--text-muted)]">{f.hint}</div>
                            </div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
